import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SupabaseLoginGate } from '../SupabaseLoginGate';
import { enumAdminClient, type EnumColumn, type EnumEnv, type EnumType } from './enum-admin-client';
import { EnumValueDialog } from './EnumValueDialog';

export function EnumAdminPage() {
  const [schema, setSchema] = useState<EnumEnv>('dev');
  const [types, setTypes] = useState<EnumType[]>([]);
  const [columnsByType, setColumnsByType] = useState<Record<string, EnumColumn[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [addDialog, setAddDialog] = useState<{ type: string } | null>(null);
  const [renameDialog, setRenameDialog] = useState<{
    type: string;
    oldValue: string;
  } | null>(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshErr, setRefreshErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setRefreshErr(null);
    try {
      const t = await enumAdminClient.listTypes(schema);
      setTypes(t);
      const cols = await Promise.all(
        t.map((tt) =>
          enumAdminClient.listColumns(schema, tt.name).then((c) => [tt.name, c] as const)
        )
      );
      setColumnsByType(Object.fromEntries(cols));
    } catch (e) {
      setRefreshErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [schema]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const needsProdConfirm = schema === 'production';

  const toggleExpand = (name: string) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });
  };

  return (
    <SupabaseLoginGate>
      <div className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <Select value={schema} onValueChange={(v) => setSchema(v as EnumEnv)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dev">dev</SelectItem>
              <SelectItem value="staging">staging</SelectItem>
              <SelectItem value="production">production ⚠</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-1" /> New enum type
          </Button>
        </div>

        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {refreshErr && (
          <div className="text-sm text-rose-600 dark:text-rose-400">Error: {refreshErr}</div>
        )}

        <div className="space-y-2">
          {types.map((t) => {
            const isOpen = expanded.has(t.name);
            const cols = columnsByType[t.name] ?? [];
            return (
              <Card key={t.name}>
                <CardHeader
                  className="flex flex-row items-center justify-between cursor-pointer"
                  onClick={() => toggleExpand(t.name)}
                >
                  <CardTitle className="text-base flex items-center gap-2">
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-mono">{t.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({t.values.length} values, used by {cols.length})
                    </span>
                  </CardTitle>
                </CardHeader>
                {isOpen && (
                  <CardContent>
                    <div className="text-xs font-semibold mb-1">Values</div>
                    <ol className="list-decimal pl-6 space-y-1">
                      {t.values.map((v) => (
                        <li key={v} className="flex items-center gap-2">
                          <code className="text-xs">{v}</code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => setRenameDialog({ type: t.name, oldValue: v })}
                            aria-label={`Rename ${v}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        </li>
                      ))}
                    </ol>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => setAddDialog({ type: t.name })}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add value
                    </Button>

                    <div className="text-xs font-semibold mt-3 mb-1">Used by</div>
                    {cols.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic">
                        no columns reference this type
                      </div>
                    ) : (
                      <ul className="text-xs space-y-0.5">
                        {cols.map((c) => (
                          <li key={`${c.table}.${c.column}`} className="font-mono">
                            {schema}.{c.table}.{c.column}
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {addDialog && (
          <EnumValueDialog
            open
            onOpenChange={(o) => !o && setAddDialog(null)}
            title={`Add value to ${addDialog.type}`}
            fieldLabel="Value (uppercase identifier)"
            needsProdConfirm={needsProdConfirm}
            onSubmit={async (val, conf) => {
              await enumAdminClient.addValue(schema, addDialog.type, val, undefined, conf);
              await refresh();
            }}
          />
        )}
        {renameDialog && (
          <EnumValueDialog
            open
            onOpenChange={(o) => !o && setRenameDialog(null)}
            title={`Rename value in ${renameDialog.type}`}
            fieldLabel={`New name for "${renameDialog.oldValue}"`}
            needsProdConfirm={needsProdConfirm}
            onSubmit={async (val, conf) => {
              await enumAdminClient.renameValue(
                schema,
                renameDialog.type,
                renameDialog.oldValue,
                val,
                conf
              );
              await refresh();
            }}
          />
        )}
        {createDialog && (
          <EnumValueDialog
            open
            onOpenChange={(o) => !o && setCreateDialog(false)}
            title="New enum type"
            fieldLabel="Format: name,VAL1,VAL2,..."
            needsProdConfirm={needsProdConfirm}
            onSubmit={async (val, conf) => {
              const [name, ...values] = val.split(',').map((s) => s.trim());
              if (!name || values.length === 0) throw new Error('format: name,VAL1,VAL2,…');
              await enumAdminClient.createType(schema, name, values, conf);
              await refresh();
            }}
          />
        )}
      </div>
    </SupabaseLoginGate>
  );
}

export default EnumAdminPage;
